(function () {
  if (window.MYD20AnniversaryLoaded) {
    if (typeof window.MYD20Init === 'function') window.MYD20Init(document);
    return;
  }
  window.MYD20AnniversaryLoaded = true;

  let youtubeApiPromise = null;
  function ensureYouTubeAPI() {
    if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
    if (youtubeApiPromise) return youtubeApiPromise;

    youtubeApiPromise = new Promise((resolve, reject) => {
      let script = document.querySelector('script[src*="youtube.com/iframe_api"]');
      if (!script) {
        script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        document.head.appendChild(script);
      }
      const started = Date.now();
      const poll = window.setInterval(() => {
        if (window.YT && window.YT.Player) {
          window.clearInterval(poll);
          resolve(window.YT);
        } else if (Date.now() - started > 12000) {
          window.clearInterval(poll);
          reject(new Error('YouTube API timeout'));
        }
      }, 60);
    });
    return youtubeApiPromise;
  }

  function mediaOwnerVisible(element) {
    if (!element) return false;
    const owner = element.matches?.('video, .myd20-youtube') ? element : element.closest?.('.myd20-youtube, video');
    if (!owner) return false;
    const styles = getComputedStyle(owner);
    return styles.display !== 'none' && styles.visibility !== 'hidden' && owner.getClientRects().length > 0;
  }

  let shopifyPreviewObserver = null;
  let shopifySmartWarmObserver = null;
  let shopifySmartBufferObserver = null;

  function isSmartPopupVideo(video) {
    return !!video?.classList?.contains('myd20-media-preview-video--smart-popup');
  }

  function setSmartShopifyPreload(video, mode) {
    if (!video || !isSmartPopupVideo(video) || video.classList.contains('myd20-viewer-video--moved')) return;
    const rank = { none: 0, metadata: 1, auto: 2 };
    const current = video.dataset.mydPreloadMode || 'none';
    if ((rank[mode] ?? 0) <= (rank[current] ?? 0)) return;

    video.dataset.mydPreloadMode = mode;
    video.preload = mode;
    video.setAttribute('preload', mode);

    // Safari/iOS can ignore a late preload change until load() is called.
    // Only do it before decoding starts so an existing buffer is never reset.
    if (video.readyState === 0) {
      try { video.load(); } catch (_) {}
    }
  }

  function warmSmartShopifyVideo(video, aggressive) {
    setSmartShopifyPreload(video, aggressive ? 'auto' : 'metadata');
  }

  function attemptShopifyPreviewPlay(video) {
    // Smart-popup Colombiamoda videos intentionally remain visually as posters.
    // They buffer invisibly and are moved into the popup on click.
    if (!video || isSmartPopupVideo(video) || video.classList.contains('myd20-viewer-video--moved') || !mediaOwnerVisible(video)) return;
    try {
      video.muted = true;
      video.defaultMuted = true;
      video.autoplay = true;
      video.loop = true;
      video.playsInline = true;
      video.controls = false;
      video.preload = 'auto';
      video.setAttribute('muted', '');
      video.setAttribute('autoplay', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
    } catch (_) {}
  }

  function ensureShopifyPreviewObservers() {
    if (!shopifyPreviewObserver && 'IntersectionObserver' in window) {
      // Legacy previews elsewhere on the anniversary page keep their original
      // autoplay behavior. V13 only changes Colombiamoda smart-popup videos.
      shopifyPreviewObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio > 0.08 && !document.hidden) {
            attemptShopifyPreviewPlay(video);
          } else {
            try { video.pause(); } catch (_) {}
          }
        });
      }, { rootMargin: '180px 0px', threshold: [0, 0.08, 0.35] });
    }

    if (!shopifySmartWarmObserver && 'IntersectionObserver' in window) {
      // Cheap first stage: only metadata while Colombiamoda is approaching.
      shopifySmartWarmObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) warmSmartShopifyVideo(entry.target, false);
        });
      }, { rootMargin: '600px 0px', threshold: 0 });
    }

    if (!shopifySmartBufferObserver && 'IntersectionObserver' in window) {
      // Full buffering only for a card that is on/very near the viewport.
      // The poster stays visible: no autoplay and no layout/visual change.
      shopifySmartBufferObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) warmSmartShopifyVideo(entry.target, true);
        });
      }, { rootMargin: '120px 0px', threshold: 0 });
    }
  }

  function initShopifyPreviewVideos(scope) {
    const base = scope || document;
    const videos = [];
    if (base.matches?.('.myd20-media-preview-video')) videos.push(base);
    base.querySelectorAll?.('.myd20-media-preview-video').forEach((video) => videos.push(video));
    if (!videos.length) return;

    ensureShopifyPreviewObservers();

    videos.forEach((video) => {
      if (video.dataset.mydShopifyPreviewReady === 'true') return;
      video.dataset.mydShopifyPreviewReady = 'true';

      if (isSmartPopupVideo(video)) {
        video.dataset.mydPreloadMode = 'none';
        video.muted = true;
        video.defaultMuted = true;
        video.autoplay = false;
        video.loop = true;
        video.playsInline = true;
        video.controls = false;
        video.preload = 'none';
        video.removeAttribute('autoplay');
        video.setAttribute('muted', '');
        video.setAttribute('loop', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.setAttribute('preload', 'none');

        const owner = video.closest('[data-myd-gallery-item]');
        const warmOnIntent = () => warmSmartShopifyVideo(video, true);
        owner?.addEventListener('pointerenter', warmOnIntent, { passive: true });
        owner?.addEventListener('pointerdown', warmOnIntent, { passive: true });
        owner?.addEventListener('touchstart', warmOnIntent, { passive: true });
        owner?.addEventListener('focusin', warmOnIntent);
        video.addEventListener('error', () => video.classList.remove('is-myd-playing'));

        if (shopifySmartWarmObserver) shopifySmartWarmObserver.observe(video);
        else warmSmartShopifyVideo(video, false);
        if (shopifySmartBufferObserver) shopifySmartBufferObserver.observe(video);
        return;
      }

      // Existing/legacy preview behavior remains exactly as before V13.
      video.muted = true;
      video.defaultMuted = true;
      video.autoplay = true;
      video.loop = true;
      video.playsInline = true;
      video.controls = false;
      video.preload = 'auto';
      video.setAttribute('muted', '');
      video.setAttribute('autoplay', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      const markPlaying = () => video.classList.add('is-myd-playing');
      video.addEventListener('playing', markPlaying);
      video.addEventListener('loadeddata', () => {
        if (!document.hidden) attemptShopifyPreviewPlay(video);
      });
      video.addEventListener('canplay', () => {
        if (!document.hidden) attemptShopifyPreviewPlay(video);
      });
      video.addEventListener('error', () => video.classList.remove('is-myd-playing'));

      if (shopifyPreviewObserver) shopifyPreviewObserver.observe(video);
      else attemptShopifyPreviewPlay(video);
    });
  }

  function pausePreviewMedia(scope) {
    scope?.querySelectorAll?.('.myd20-media-preview-video').forEach((video) => {
      try { video.pause(); } catch (_) {}
    });
  }

  function resumePreviewMedia(scope) {
    initShopifyPreviewVideos(scope);
    scope?.querySelectorAll?.('.myd20-media-preview-video:not(.myd20-media-preview-video--smart-popup)').forEach((video) => attemptShopifyPreviewPlay(video));
  }

  function commandYouTubePreview(scope, command) {
    scope?.querySelectorAll?.('.myd20-youtube-preview').forEach((iframe) => {
      try {
        iframe.contentWindow?.postMessage(JSON.stringify({
          event: 'command',
          func: command,
          args: []
        }), '*');
      } catch (_) {}
    });
  }

  function pauseMomentPreviews(scope) {
    pausePreviewMedia(scope);
    commandYouTubePreview(scope, 'pauseVideo');
  }

  function resumeMomentPreviews(scope) {
    resumePreviewMedia(scope);
    commandYouTubePreview(scope, 'mute');
    commandYouTubePreview(scope, 'playVideo');
  }


  let youtubeConnectionsWarmed = false;
  function warmYouTubeConnections() {
    if (youtubeConnectionsWarmed) return;
    youtubeConnectionsWarmed = true;
    [
      'https://www.youtube-nocookie.com',
      'https://www.youtube.com',
      'https://i.ytimg.com',
      'https://i9.ytimg.com'
    ].forEach((href) => {
      if (document.head.querySelector(`link[rel="preconnect"][href="${href}"]`)) return;
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });
  }

  function initCarousel(root) {
    // Gallery remains a real slider on desktop/tablet/mobile.
    const track = root.querySelector('[data-myd-carousel-track]');
    if (!track || track.dataset.ready === 'true') return;
    track.dataset.ready = 'true';

    const prev = root.querySelector('[data-myd-carousel-prev]');
    const next = root.querySelector('[data-myd-carousel-next]');
    const dots = Array.from(root.querySelectorAll('[data-myd-carousel-dot]'));
    const cards = Array.from(track.children).filter((node) => node.nodeType === 1);
    const autoplay = root.dataset.autoplay === 'true';
    const seconds = Math.max(2, parseFloat(root.dataset.autoplaySeconds || '6'));
    let timer = null;
    let pausedByInteraction = false;

    const cardStep = () => {
      if (!cards.length) return track.clientWidth;
      const card = cards[0];
      const styles = getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || 0);
      return card.getBoundingClientRect().width + gap;
    };

    const maxIndex = () => {
      if (!cards.length) return 0;
      const step = Math.max(cardStep(), 1);
      return Math.max(0, Math.round((track.scrollWidth - track.clientWidth) / step));
    };

    const currentIndex = () => {
      if (!cards.length) return 0;
      return Math.max(0, Math.min(maxIndex(), Math.round(track.scrollLeft / Math.max(cardStep(), 1))));
    };

    function goTo(index, smooth) {
      if (!cards.length) return;
      const last = maxIndex();
      const normalized = last === 0 ? 0 : ((index % (last + 1)) + (last + 1)) % (last + 1);
      track.scrollTo({ left: normalized * cardStep(), behavior: smooth === false ? 'auto' : 'smooth' });
    }

    function go(direction) {
      const index = currentIndex();
      const last = maxIndex();
      if (last === 0) return;
      if (direction > 0 && index >= last) return goTo(0, true);
      if (direction < 0 && index <= 0) return goTo(last, true);
      goTo(index + direction, true);
    }
    prev?.addEventListener('click', () => { pausedByInteraction = true; go(-1); restartTimer(); });
    next?.addEventListener('click', () => { pausedByInteraction = true; go(1); restartTimer(); });

    function updateDots() {
      if (!dots.length) return;
      const index = currentIndex();
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    }
    track.addEventListener('scroll', () => window.requestAnimationFrame(updateDots), { passive: true });

    function clearTimer() {
      if (timer) window.clearInterval(timer);
      timer = null;
    }
    function startTimer() {
      if (!autoplay || cards.length < 2 || document.hidden) return;
      clearTimer();
      timer = window.setInterval(() => go(1), seconds * 1000);
    }
    function restartTimer() {
      clearTimer();
      window.setTimeout(() => { pausedByInteraction = false; startTimer(); }, Math.min(seconds * 600, 3000));
    }

    root.addEventListener('mouseenter', clearTimer);
    root.addEventListener('mouseleave', () => { if (!pausedByInteraction) startTimer(); });
    root.addEventListener('focusin', clearTimer);
    root.addEventListener('focusout', () => { if (!pausedByInteraction) startTimer(); });
    track.addEventListener('pointerdown', clearTimer, { passive: true });
    track.addEventListener('pointerup', restartTimer, { passive: true });
    document.addEventListener('visibilitychange', () => document.hidden ? clearTimer() : startTimer());

    updateDots();
    startTimer();
  }

  function stopStageMedia(stage) {
    if (!stage) return;
    stage.querySelectorAll('video').forEach((video) => {
      try { video.pause(); } catch (_) {}
    });
    stage.querySelectorAll('iframe').forEach((iframe) => { iframe.src = ''; });
  }

  function activateStageMedia(stage) {
    if (!stage) return;
    const iframe = stage.querySelector('iframe[data-myd-viewer-youtube], iframe[data-src]');
    if (iframe && iframe.dataset.src && !iframe.src) iframe.src = iframe.dataset.src;

    const video = stage.querySelector('video');
    if (video) {
      video.muted = false;
      video.volume = 1;
      video.controls = true;
      try {
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch (_) {}
    }
  }

  function initGallery(root) {
    const items = Array.from(root.querySelectorAll('[data-myd-gallery-item]'));
    const lightbox = root.querySelector('[data-myd-lightbox]');
    if (!items.length || !lightbox || lightbox.dataset.ready === 'true') return;
    lightbox.dataset.ready = 'true';

    const stage = lightbox.querySelector('[data-myd-lightbox-stage]') || lightbox.querySelector('.myd20-lightbox__stage');
    const close = lightbox.querySelector('[data-myd-lightbox-close]');
    const prev = lightbox.querySelector('[data-myd-lightbox-prev]');
    const next = lightbox.querySelector('[data-myd-lightbox-next]');
    const isMomentGallery = root.dataset.mydGalleryMode === 'moment';
    let index = 0;
    let lastFocus = null;
    let touchStartX = null;
    const imagePreloadCache = new Map();
    let viewerYouTubePlayer = null;
    let movedShopifyVideo = null;

    function restoreMovedShopifyVideo() {
      if (!movedShopifyVideo) return;
      const { video, parent, nextSibling } = movedShopifyVideo;
      try { video.pause(); } catch (_) {}
      const smartPopupVideo = isSmartPopupVideo(video);
      video.controls = false;
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.autoplay = !smartPopupVideo;
      video.playsInline = true;
      if (smartPopupVideo) video.removeAttribute('autoplay');
      video.classList.remove('myd20-viewer-video', 'myd20-viewer-video--moved');
      if (parent) {
        if (nextSibling && nextSibling.parentNode === parent) parent.insertBefore(video, nextSibling);
        else parent.appendChild(video);
      }
      movedShopifyVideo = null;
      if (smartPopupVideo) {
        if (shopifySmartWarmObserver) shopifySmartWarmObserver.observe(video);
        if (shopifySmartBufferObserver) shopifySmartBufferObserver.observe(video);
        else warmSmartShopifyVideo(video, false);
      } else {
        if (shopifyPreviewObserver) shopifyPreviewObserver.observe(video);
        else attemptShopifyPreviewPlay(video);
      }
    }

    const lightboxHomeParent = lightbox.parentNode;
    const lightboxHomeNext = lightbox.nextSibling;

    function mountLightboxToViewport() {
      // Escape section overflow/stacking contexts while open. This makes the
      // viewer center against the actual viewport, not the Colombiamoda grid.
      if (lightbox.parentNode !== document.body) document.body.appendChild(lightbox);
    }

    function restoreLightboxHome() {
      if (!lightboxHomeParent || lightbox.parentNode === lightboxHomeParent) return;
      if (lightboxHomeNext && lightboxHomeNext.parentNode === lightboxHomeParent) {
        lightboxHomeParent.insertBefore(lightbox, lightboxHomeNext);
      } else {
        lightboxHomeParent.appendChild(lightbox);
      }
    }

    function destroyViewerYouTubePlayer() {
      if (!viewerYouTubePlayer) return;
      try { viewerYouTubePlayer.destroy(); } catch (_) {}
      viewerYouTubePlayer = null;
    }

    function resetStagePresentation() {
      if (!stage) return;
      stage.classList.remove('myd20-lightbox__stage--youtube-loading');
      stage.style.removeProperty('background-image');
      stage.style.removeProperty('background-size');
      stage.style.removeProperty('background-position');
      stage.style.removeProperty('background-repeat');
    }

    function renderMediaName(item) {
      if (!stage || !item) return;
      const name = (item.dataset.mydMediaName || '').trim();
      if (!name) return;
      const label = document.createElement('div');
      label.className = 'myd20-lightbox__media-name';
      label.innerHTML = '<span aria-hidden="true"></span>';
      label.appendChild(document.createTextNode(name));
      stage.appendChild(label);
    }

    function preloadGalleryImage(item) {
      if (!item || item.dataset.mydMediaType !== 'image') return null;
      const fullSrc = item.dataset.mydFullSrc;
      if (!fullSrc) return null;
      if (imagePreloadCache.has(fullSrc)) return imagePreloadCache.get(fullSrc);

      const preload = new Image();
      preload.decoding = 'async';
      preload.loading = 'eager';
      preload.src = fullSrc;
      preload.decode?.().catch(() => {});
      imagePreloadCache.set(fullSrc, preload);
      return preload;
    }

    function warmAdjacentImages(currentIndex) {
      if (items.length < 2) return;
      preloadGalleryImage(items[(currentIndex + 1) % items.length]);
      preloadGalleryImage(items[(currentIndex - 1 + items.length) % items.length]);
      preloadGalleryImage(items[(currentIndex + 2) % items.length]);
    }

    if (items.length < 2) {
      if (prev) prev.hidden = true;
      if (next) next.hidden = true;
    }

    function renderItem(item) {
      if (!stage) return;
      restoreMovedShopifyVideo();
      destroyViewerYouTubePlayer();
      stopStageMedia(stage);

      // COLOMBIAMODA ONLY: one active media node per navigation step.
      // This prevents a previous YouTube player/video from remaining under
      // the next image/video. The separate Gallery lightbox is untouched.
      if (isMomentGallery) {
        stage.replaceChildren();
        stage.classList.remove('myd20-lightbox__stage--moment-image');
      }

      resetStagePresentation();
      stage.classList.remove('myd20-lightbox__stage--moment-youtube');

      // MYD Colombiamoda — YouTube popup uses the same IFrame Player API base
      // The silent preview is NEVER cloned into the popup: one click => one player.
      if (isMomentGallery && item.dataset.mydMediaType === 'youtube' && item.dataset.mydYoutubeId) {
        warmYouTubeConnections();
        // Preconnect aggressively only when user opens Colombiamoda video.
        const youtubeId = item.dataset.mydYoutubeId;

        stage.classList.add('myd20-lightbox__stage--moment-youtube');

        const shell = document.createElement('div');
        shell.className = 'myd20-moment-youtube-popup is-loading';

        // Instant visual feedback: show YouTube thumbnail while the player API loads.
        // This avoids an empty popup on mobile Chrome.
        const poster = document.createElement('img');
        poster.className = 'myd20-youtube-popup-poster';
        poster.alt = item.getAttribute('aria-label') || 'Video MYD Colombiamoda';
        poster.loading = 'eager';
        poster.decoding = 'async';
        poster.fetchPriority = 'high';

        // Prefer the optional Shopify-hosted cover. It is served from the
        // store CDN and appears much faster on mobile/desktop than waiting
        // for YouTube's maxres thumbnail.
        const customPoster = item.dataset.mydYoutubePoster || '';
        poster.src = customPoster || `https://i.ytimg.com/vi/${encodeURIComponent(youtubeId)}/hqdefault.jpg`;
        shell.appendChild(poster);

        // Player loads behind the preview and reveals only when ready.
        const playerHost = document.createElement('div');
        playerHost.id = `MYD20MomentPopupYT-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        playerHost.className = 'myd20-moment-youtube-popup__host';
        shell.appendChild(playerHost);
        stage.appendChild(shell);

        const revealPlayer = () => {
          if (!stage.contains(shell)) return;
          shell.classList.remove('is-loading');
          shell.classList.add('is-player-ready');
        };

        ensureYouTubeAPI().then((YT) => {
          if (!stage.contains(shell)) return;
          try {
            viewerYouTubePlayer = new YT.Player(playerHost.id, {
              videoId: youtubeId,
              playerVars: {
                autoplay: 1,
                mute: 1,
                controls: 1,
                playsinline: 1,
                rel: 0,
                fs: 1,
                iv_load_policy: 3,
                origin: window.location.origin
              },
              events: {
                onReady(event) {
                  if (!stage.contains(shell)) return;
                  try {
                    // Muted autoplay is the reliable path on Chrome/Safari mobile.
                    // The visitor can enable sound with YouTube's native controls.
                    event.target.mute?.();
                    event.target.setVolume?.(0);
                    event.target.playVideo?.();
                  } catch (_) {}

                  // Keep the instant poster until YouTube has actually painted a
                  // playing/buffering frame. This prevents a black flash.
                  window.setTimeout(() => {
                    if (!stage.contains(shell)) return;
                    try {
                      const state = event.target.getPlayerState?.();
                      if (state === YT.PlayerState.PLAYING || state === YT.PlayerState.BUFFERING) {
                        revealPlayer();
                      }
                    } catch (_) {}
                  }, 120);
                  // Last-resort reveal: never leave the user looking at a poster
                  // forever if YouTube takes longer or returns a non-playing state.
                  window.setTimeout(() => {
                    if (stage.contains(shell)) revealPlayer();
                  }, 1800);
                },
                onStateChange(event) {
                  if (event.data === YT.PlayerState.PLAYING || event.data === YT.PlayerState.BUFFERING || event.data === YT.PlayerState.PAUSED) {
                    revealPlayer();
                  }
                },
                onError() {
                  // Reveal the native player even on a YouTube-side error so the
                  // visitor can see/use its own controls and error messaging.
                  revealPlayer();
                }
              }
            });
          } catch (_) {
            revealPlayer();
          }
        }).catch(() => {
          // API fallback: still render exactly one native YouTube iframe.
          if (!stage.contains(shell)) return;
          playerHost.remove();
          const iframe = document.createElement('iframe');
          iframe.className = 'myd20-moment-youtube-popup__iframe';
          iframe.title = item.getAttribute('aria-label')?.replace(/^Reproducir video:\s*/i, '') || 'Video MYD Colombiamoda';
          iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(youtubeId)}?autoplay=1&mute=1&controls=1&playsinline=1&rel=0&fs=1`;
          iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
          iframe.allowFullscreen = true;
          iframe.frameBorder = '0';
          shell.appendChild(iframe);
          window.setTimeout(revealPlayer, 250);
        });
        renderMediaName(item);
        return;
      }

      // Shopify Video popup: reuse the already-buffered preview element.
      // This avoids a second network request / black first frame on the live storefront.
      if (item.dataset.mydMediaType === 'video') {
        const previewVideo = item.querySelector('.myd20-media-preview-video');
        if (previewVideo) {
          const parent = previewVideo.parentNode;
          const nextSibling = previewVideo.nextSibling;
          movedShopifyVideo = { video: previewVideo, parent, nextSibling };
          if (shopifySmartWarmObserver) shopifySmartWarmObserver.unobserve(previewVideo);
          if (shopifySmartBufferObserver) shopifySmartBufferObserver.unobserve(previewVideo);
          if (shopifyPreviewObserver) shopifyPreviewObserver.unobserve(previewVideo);
          try { previewVideo.pause(); } catch (_) {}

          const posterUrl = item.dataset.mydShopifyPoster || previewVideo.poster || '';
          let loadingPoster = null;
          if (posterUrl) {
            loadingPoster = document.createElement('img');
            loadingPoster.className = 'myd20-lightbox__loading-poster myd20-lightbox__loading-poster--shopify';
            loadingPoster.src = posterUrl;
            loadingPoster.alt = '';
            loadingPoster.loading = 'eager';
            loadingPoster.decoding = 'async';
            loadingPoster.fetchPriority = 'high';
            stage.appendChild(loadingPoster);
          }

          previewVideo.classList.add('myd20-viewer-video', 'myd20-viewer-video--moved');
          previewVideo.preload = 'auto';
          previewVideo.setAttribute('preload', 'auto');
          previewVideo.controls = true;
          previewVideo.loop = false;
          previewVideo.autoplay = false;
          previewVideo.playsInline = true;
          previewVideo.muted = false;
          previewVideo.defaultMuted = false;
          previewVideo.volume = 1;
          if (posterUrl) previewVideo.poster = posterUrl;
          stage.appendChild(previewVideo);

          let posterRevealed = false;
          const revealVideo = () => {
            if (posterRevealed) return;
            posterRevealed = true;
            if (!loadingPoster || !stage.contains(loadingPoster)) return;
            loadingPoster.classList.add('is-ready');
            window.setTimeout(() => loadingPoster?.remove(), 260);
          };
          previewVideo.addEventListener('playing', revealVideo, { once: true });
          // If autoplay is blocked, reveal the native player once it is ready so
          // the visitor can press play instead of being trapped behind a poster.
          previewVideo.addEventListener('canplay', () => {
            if (previewVideo.paused) window.setTimeout(revealVideo, 450);
          }, { once: true });
          window.setTimeout(revealVideo, 1800);

          try {
            const p = previewVideo.play();
            if (p && typeof p.catch === 'function') {
              p.catch(() => {
                // Mobile browser fallback: muted autoplay is much more reliable.
                previewVideo.muted = true;
                previewVideo.defaultMuted = true;
                const retry = previewVideo.play();
                if (retry && typeof retry.catch === 'function') retry.catch(() => revealVideo());
              });
            }
          } catch (_) {
            revealVideo();
          }
          renderMediaName(item);
          return;
        }
      }

      // Images: the lightbox uses a real single-frame swap.
      // Never move a track and never change the visible image before the next
      // frame is decoded. This prevents the "half of the next photo" artifact
      // that Chrome/Safari can show when two media nodes remain in the stage.
      if (item.dataset.mydMediaType === 'image') {
        if (isMomentGallery) {
          stage.classList.add('myd20-lightbox__stage--moment-image');
        }
        const thumb = item.querySelector('img');
        const previewSrc = thumb?.currentSrc || thumb?.src || '';
        const fullSrc = item.dataset.mydFullSrc || previewSrc;
        const previousLayers = Array.from(stage.querySelectorAll('.myd20-lightbox__image-layer'));

        const layer = document.createElement('div');
        layer.className = 'myd20-lightbox__image-layer';
        const image = document.createElement('img');
        image.alt = thumb?.alt || '';
        image.decoding = 'async';
        image.loading = 'eager';
        image.className = 'myd20-lightbox__progressive-image';
        if (previewSrc) image.src = previewSrc;
        layer.appendChild(image);
        stage.appendChild(layer);
        renderMediaName(item);

        const removeOldLayers = () => {
          previousLayers.forEach((oldLayer) => {
            if (oldLayer !== layer && oldLayer.parentNode === stage) oldLayer.remove();
          });
          // Keep only the active image layer; media labels are recreated per item.
          stage.querySelectorAll('.myd20-lightbox__media-name').forEach((label) => {
            if (!layer.contains(label)) label.remove();
          });
        };

        if (!fullSrc || fullSrc === previewSrc) {
          requestAnimationFrame(() => {
            layer.classList.add('is-ready');
            removeOldLayers();
          });
          return;
        }

        const hiRes = preloadGalleryImage(item) || new Image();
        hiRes.decoding = 'async';
        hiRes.loading = 'eager';
        if (!hiRes.src) hiRes.src = fullSrc;

        const commitFullImage = async () => {
          if (!stage.contains(layer)) return;
          try {
            if (hiRes.decode) await hiRes.decode();
          } catch (_) {}
          if (!stage.contains(layer) || !hiRes.naturalWidth) return;

          const finalImage = document.createElement('img');
          finalImage.alt = image.alt;
          finalImage.decoding = 'async';
          finalImage.loading = 'eager';
          finalImage.className = 'myd20-lightbox__progressive-image';
          finalImage.src = hiRes.src;

          try {
            if (finalImage.decode) await finalImage.decode();
          } catch (_) {}

          if (!stage.contains(layer)) return;
          layer.appendChild(finalImage);
          image.remove();
          layer.classList.add('is-ready');
          removeOldLayers();
        };

        if (hiRes.complete && hiRes.naturalWidth) {
          commitFullImage();
        } else {
          hiRes.addEventListener('load', commitFullImage, { once: true });
          hiRes.addEventListener('error', () => {
            if (stage.contains(layer)) {
              layer.classList.add('is-ready');
              removeOldLayers();
            }
          }, { once: true });
        }
        return;
      }

      const template = item.querySelector('template[data-myd-media-template]');
      if (template) {
        const fragment = template.content.cloneNode(true);
        const iframe = fragment.querySelector('iframe[data-myd-viewer-youtube], iframe[data-src]');

        if (iframe) {
          warmYouTubeConnections();
          const dataSrc = iframe.dataset.src || '';
          if (dataSrc) iframe.src = dataSrc;
          stage.appendChild(fragment);
          activateStageMedia(stage);
          renderMediaName(item);
          return;
        }

        stage.appendChild(fragment);
        activateStageMedia(stage);
        return;
      }

      const src = item.dataset.full || item.querySelector('img')?.src || '';
      if (src) {
        const image = document.createElement('img');
        image.src = src;
        image.alt = item.dataset.alt || item.querySelector('img')?.alt || '';
        stage.appendChild(image);
        renderMediaName(item);
      }
    }

    function show(i) {
      index = (i + items.length) % items.length;
      renderItem(items[index]);
      warmAdjacentImages(index);
    }

    function open(i) {
      lastFocus = document.activeElement;
      if (isMomentGallery) pauseMomentPreviews(root);
      else pausePreviewMedia(root);

      // Colombiamoda is mounted directly under <body> while open so no section
      // overflow/grid/stacking context can offset or clip the popup.
      mountLightboxToViewport();
      lightbox.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      show(i);
      close?.focus();
    }

    function closeBox() {
      destroyViewerYouTubePlayer();
      restoreMovedShopifyVideo();
      stopStageMedia(stage);
      stage?.replaceChildren();
      resetStagePresentation();
      lightbox.hidden = true;
      document.documentElement.style.overflow = '';
      restoreLightboxHome();
      if (isMomentGallery) resumeMomentPreviews(root);
      else resumePreviewMedia(root);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    items.forEach((item, i) => {
      const warmItem = () => {
        preloadGalleryImage(item);
        if (item.dataset.mydMediaType === 'video') {
          const previewVideo = item.querySelector('.myd20-media-preview-video');
          if (previewVideo && isSmartPopupVideo(previewVideo)) warmSmartShopifyVideo(previewVideo, true);
        }
        if (item.dataset.mydMediaType === 'youtube' && item.dataset.mydYoutubeId) {
          warmYouTubeConnections();
          // Start loading the IFrame API before the click when the user
          // touches/points at a YouTube card. The popup can then create the
          // player immediately after the gesture.
          ensureYouTubeAPI().catch(() => {});
          return;
        }
        const template = item.querySelector('template[data-myd-media-template]');
        if (template?.content?.querySelector('iframe[data-myd-viewer-youtube], iframe[data-src]')) warmYouTubeConnections();
      };
      item.addEventListener('pointerenter', warmItem, { passive: true });
      item.addEventListener('focus', warmItem);
      item.addEventListener('touchstart', warmItem, { passive: true, once: true });
      item.addEventListener('click', (event) => {
        event.preventDefault();
        open(i);
      });
    });
    close?.addEventListener('click', closeBox);
    prev?.addEventListener('click', () => show(index - 1));
    next?.addEventListener('click', () => show(index + 1));
    lightbox.addEventListener('click', (event) => { if (event.target === lightbox) closeBox(); });
    lightbox.addEventListener('touchstart', (event) => { touchStartX = event.touches[0]?.clientX ?? null; }, { passive: true });
    lightbox.addEventListener('touchend', (event) => {
      if (touchStartX === null) return;
      const endX = event.changedTouches[0]?.clientX ?? touchStartX;
      const delta = endX - touchStartX;
      if (Math.abs(delta) > 45 && items.length > 1) show(index + (delta < 0 ? 1 : -1));
      touchStartX = null;
    }, { passive: true });

    document.addEventListener('keydown', (event) => {
      if (lightbox.hidden) return;
      if (event.key === 'Escape') closeBox();
      if (event.key === 'ArrowLeft' && items.length > 1) show(index - 1);
      if (event.key === 'ArrowRight' && items.length > 1) show(index + 1);
    });
  }



  function initInternalLinks(scope) {
    const base = scope || document;
    const links = [];
    if (base.matches?.('[data-myd-scroll-to]')) links.push(base);
    base.querySelectorAll?.('[data-myd-scroll-to]').forEach((link) => links.push(link));
    links.forEach((link) => {
      if (link.dataset.mydScrollReady === 'true') return;
      link.dataset.mydScrollReady = 'true';
      link.addEventListener('click', (event) => {
        const key = link.dataset.mydScrollTo;
        const target = document.querySelector(`[data-myd-anchor="${key}"]`);
        if (!target) return;
        event.preventDefault();
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        if (history?.replaceState) history.replaceState(null, '', `#myd-${key}`);
      });
    });
  }

  function fitTextElement(element) {
    const desired = parseFloat(element.dataset.mydFontSize || '0');
    if (!desired) return;
    const minimum = parseFloat(element.dataset.mydMinFontSize || '10');
    element.style.fontSize = desired + 'px';
    let current = desired;
    let attempts = 0;
    while (element.scrollWidth > element.clientWidth + 1 && current > minimum && attempts < 180) {
      current -= 1;
      element.style.fontSize = current + 'px';
      attempts += 1;
    }
  }

  function fitCustomText(scope) {
    const base = scope || document;
    const nodes = [];
    if (base.matches?.('[data-myd-autofit]')) nodes.push(base);
    base.querySelectorAll?.('[data-myd-autofit]').forEach((node) => nodes.push(node));
    nodes.forEach(fitTextElement);
  }

  let resizeFrame = null;
  function scheduleFit() {
    if (resizeFrame) cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => fitCustomText(document));
  }

  function init(scope) {
    const base = scope || document;
    base.querySelectorAll?.('[data-myd-carousel]').forEach(initCarousel);
    base.querySelectorAll?.('[data-myd-gallery]').forEach(initGallery);
initInternalLinks(base);
    if (base.matches?.('[data-myd-carousel]')) initCarousel(base);
    if (base.matches?.('[data-myd-gallery]')) initGallery(base);
fitCustomText(base);
    initShopifyPreviewVideos(base);
    resumePreviewMedia(base);
  }

  window.MYD20Init = init;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  window.addEventListener('resize', scheduleFit, { passive: true });
  window.addEventListener('pageshow', () => {
    initShopifyPreviewVideos(document);
    resumePreviewMedia(document);
  });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      initShopifyPreviewVideos(document);
      resumePreviewMedia(document);
    }
  });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => fitCustomText(document));
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();


// Performance patch: lazy media
// Delays background videos until the browser is idle.
(function(){
  function deferVideos(){
    document.querySelectorAll('video[preload="none"]').forEach(function(v){
      if(!v.dataset.mydDeferred){
        v.dataset.mydDeferred='1';
        v.load();
      }
    });
  }
  if('requestIdleCallback' in window){
    requestIdleCallback(deferVideos,{timeout:2500});
  } else {
    window.addEventListener('load',deferVideos);
  }
})();


/* v7: Stable lightbox navigation.
   Do not translate a horizontal track. Keep exactly one visible image
   and preload/decode the next image before swapping it. */
(function () {
  function initStableLightbox() {
    const root = document.querySelector('.myd20-lightbox');
    if (!root || root.dataset.v7Stable === '1') return;
    root.dataset.v7Stable = '1';

    const stage = root.querySelector('.myd20-lightbox__stage');
    const image = root.querySelector('.myd20-lightbox__image');
    if (!stage || !image) return;

    const nextButton = root.querySelector('[data-lightbox-next]');
    const prevButton = root.querySelector('[data-lightbox-prev]');
    const items = Array.from(document.querySelectorAll('[data-myd20-gallery-image]'));
    if (!items.length) return;

    let index = 0;
    let busy = false;

    function getUrl(i) {
      const el = items[i];
      return el && (el.currentSrc || el.src || el.dataset.src || el.getAttribute('data-image-url'));
    }

    function setVisible(src) {
      if (!src) return Promise.resolve();
      return new Promise((resolve) => {
        const preloader = new Image();
        preloader.onload = () => {
          image.src = src;
          image.removeAttribute('srcset');
          image.style.opacity = '0';
          requestAnimationFrame(() => {
            image.style.transition = 'opacity 180ms ease';
            image.style.opacity = '1';
          });
          resolve();
        };
        preloader.onerror = resolve;
        preloader.src = src;
      });
    }

    async function go(delta) {
      if (busy || !items.length) return;
      const next = (index + delta + items.length) % items.length;
      const src = getUrl(next);
      if (!src) return;

      busy = true;
      try {
        await setVisible(src);
        index = next;
      } finally {
        busy = false;
      }
    }

    nextButton && nextButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      go(1);
    }, true);

    prevButton && prevButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      go(-1);
    }, true);

    root.addEventListener('click', (e) => {
      const next = e.target.closest('[data-lightbox-next]');
      const prev = e.target.closest('[data-lightbox-prev]');
      if (next || prev) e.stopImmediatePropagation();
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStableLightbox);
  } else {
    initStableLightbox();
  }
})();


/* v8: Mobile preview warm-up.
   The gallery keeps lazy loading for off-screen media, but the first visible
   previews are fetched immediately at Shopify-served size. This avoids the
   "blank for seconds" effect on Chrome/Safari mobile without downloading the
   entire gallery. */
(function () {
  function warmImage(img, url) {
    if (!img || !url || img.dataset.mydPreviewWarmed === '1') return;
    img.dataset.mydPreviewWarmed = '1';
    const pre = new Image();
    pre.decoding = 'async';
    pre.src = url;
  }

  function warmGallery(root) {
    const items = Array.from(root.querySelectorAll('[data-myd-gallery-item]'));
    if (!items.length) return;

    // First two are the only immediately critical gallery previews.
    items.slice(0, 2).forEach((item) => {
      const img = item.querySelector('img');
      const url = item.dataset.mydPreviewSrc || img?.currentSrc || img?.src;
      warmImage(img, url);

      const video = item.querySelector('video[preload="none"]');
      if (video) {
        video.preload = 'metadata';
        try { video.load(); } catch (_) {}
      }
    });

    // Warm the next item shortly after first paint, but only one.
    const warmNext = () => {
      const third = items[2];
      if (!third) return;
      const img = third.querySelector('img');
      const url = third.dataset.mydPreviewSrc || img?.currentSrc || img?.src;
      warmImage(img, url);
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(warmNext, { timeout: 900 });
    } else {
      setTimeout(warmNext, 500);
    }
  }

  function init() {
    document.querySelectorAll('[data-myd-gallery]').forEach(warmGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-myd-gallery]').forEach(warmGallery);
  });
})();


/* v11: YouTube thumbnail fast path.
   Runtime-only: no section-level markup changes, so Shopify editor/schema
   behavior remains identical to the stable v8 package. */
(function () {
  function addPreconnect(href) {
    if (document.querySelector('link[rel="preconnect"][href="' + href + '"]')) return;
    var link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = href;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }

  function warmFirstYouTubePreview() {
    var thumbs = Array.prototype.slice.call(
      document.querySelectorAll('[data-myd-youtube-thumb]')
    );
    if (!thumbs.length) return;

    addPreconnect('https://i.ytimg.com');
    addPreconnect('https://www.youtube.com');

    var first = thumbs[0];
    var url = first.getAttribute('data-myd-youtube-thumb');
    if (url) {
      var img = new Image();
      img.decoding = 'async';
      img.fetchPriority = 'high';
      img.src = url;
    }

    if (thumbs[1]) {
      var secondUrl = thumbs[1].getAttribute('data-myd-youtube-thumb');
      var warmSecond = function () {
        if (!secondUrl) return;
        var second = new Image();
        second.decoding = 'async';
        second.src = secondUrl;
      };
      if (window.requestIdleCallback) {
        window.requestIdleCallback(warmSecond, { timeout: 1000 });
      } else {
        window.setTimeout(warmSecond, 600);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', warmFirstYouTubePreview, { once: true });
  } else {
    warmFirstYouTubePreview();
  }
})();


/* v13: Fast Colombiamoda cover.
   If an optional Shopify poster exists, warm it on intent and use it as the
   visual cover. Without one, use YouTube hqdefault rather than maxresdefault
   so the fallback arrives faster. */
(function () {
  function initFastCover() {
    var item = document.querySelector('[data-myd-gallery-item][data-myd-media-type="youtube"][data-myd-youtube-poster]');
    if (!item) return;

    var url = item.getAttribute('data-myd-youtube-poster');
    if (!url) return;

    var preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = new URL(url, window.location.href).origin;
    preconnect.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect);

    var img = new Image();
    img.decoding = 'async';
    img.fetchPriority = 'high';
    img.src = url;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFastCover, {once:true});
  } else {
    initFastCover();
  }
})();

/* FINAL · Colombiamoda infinite navigation fallback */
(() => {
  const bindInfiniteMedia = (root = document) => {
    root.querySelectorAll?.('.myd20-moment__media--slider[data-myd-carousel]').forEach(slider => {
      if (slider.dataset.mydInfiniteBound === 'true') return;
      const items = slider.querySelectorAll('[data-myd-carousel-item]');
      const next = slider.querySelector('[data-myd-carousel-next]');
      const prev = slider.querySelector('[data-myd-carousel-prev]');
      if (items.length < 2 || !next || !prev) return;

      const getIndex = () => {
        const active = slider.querySelector('[data-myd-carousel-item].is-active');
        return Math.max(0, Array.from(items).indexOf(active));
      };

      next.addEventListener('click', () => {
        if (getIndex() === items.length - 1) {
          const first = items[0];
          first?.click?.();
        }
      }, true);

      prev.addEventListener('click', () => {
        if (getIndex() === 0) {
          const last = items[items.length - 1];
          last?.click?.();
        }
      }, true);

      slider.dataset.mydInfiniteBound = 'true';
    });
  };
  document.addEventListener('DOMContentLoaded', () => bindInfiniteMedia());
  document.addEventListener('shopify:section:load', e => bindInfiniteMedia(e.target));
})();
